import React, { useState, useEffect } from 'react';
import { bool, func, object, string } from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from '../../util/reactIntl';
import { ensureListing } from '../../util/data';
import { getSubscriptionTier } from '../../config/subscriptionConfig';

import SubscriptionCheckoutModal from '../../containers/EditListingPage/EditListingWizard/SubscriptionCheckoutModal';
import { PrimaryButton } from '../../components';

import css from './SubscriptionPaymentButton.module.css';

/**
 * SubscriptionPaymentButton - Shows payment button if listing needs subscription payment
 *
 * Used in:
 * - ListingPage topbar (for listing owner)
 * - EditListingPage topbar (for listing owner)
 *
 * Flow:
 * 1. Check if listing requires subscription
 * 2. Check if subscription is already paid
 * 3. If payment needed, show button
 * 4. Button opens SubscriptionCheckoutModal
 * 5. After payment, listing becomes active
 */
const SubscriptionPaymentButton = props => {
  const {
    className = null,
    rootClassName = null,
    listing,
    currentUser,
    onManageDisableScrolling = () => { },
    onPaymentSuccess = null,
    showInline = false,
  } = props;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [needsPayment, setNeedsPayment] = useState(false);

  const currentListing = ensureListing(listing);
  const publicData = currentListing.attributes?.publicData || {};

  useEffect(() => {
    const category = publicData.categoryLevel1;
    const adLevel = publicData.listing_level;

    // ============================================================
    // LEGACY GATE (pre-2026-04) — PRESERVED FOR POTENTIAL REVERT
    // Original behavior: showed Stripe payment button for ANY tier
    // in SUBSCRIPTION_TIERS, including the free categories
    // (parts, books/art/clothing, watches/memorabilia, models/toys,
    // cars-standard). This meant free listings incorrectly prompted
    // the owner for a credit card while in pendingApproval.
    // Per client request (Apr 2026, Ross via Slack): free tiers
    // must skip Stripe entirely; admin approval only.
    // To revert: (1) swap the active block below with this legacy
    // block, (2) re-add `isValidSubscriptionTier` to the import.
    //
    //   import { isValidSubscriptionTier } from '../../config/subscriptionConfig';
    //   const requiresSubscription = isValidSubscriptionTier(category, adLevel);
    //   const hasActiveSubscription =
    //     publicData.subscriptionStatus === 'active' && publicData.subscriptionActive;
    //   setNeedsPayment(requiresSubscription && !hasActiveSubscription);
    // ============================================================

    const tier = getSubscriptionTier(category, adLevel);
    const requiresPaidSubscription = !!tier && !tier.isFree;
    const hasActiveSubscription =
      publicData.subscriptionStatus === 'active' && publicData.subscriptionActive;

    setNeedsPayment(requiresPaidSubscription && !hasActiveSubscription);
  }, [publicData]);

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleSubscriptionSuccess = () => {
    setIsModalOpen(false);
    setNeedsPayment(false);

    if (onPaymentSuccess) {
      onPaymentSuccess();
    }
  };

  const handleDirectPublish = (listingId) => {
    // This function is called when using existing subscription
    setIsModalOpen(false);
    setNeedsPayment(false);

    if (onPaymentSuccess) {
      onPaymentSuccess();
    }
  };

  if (!needsPayment) {
    return null;
  }

  const classes = classNames(rootClassName || css.root, className);

  return (
    <div className={classes}>
      {showInline ? (
        <div className={css.inlineContainer}>
          <p className={css.message}>
            <FormattedMessage id="SubscriptionPaymentButton.paymentRequired" />
          </p>
          <PrimaryButton
            className={css.paymentButton}
            onClick={handleOpenModal}
          >
            <FormattedMessage id="SubscriptionPaymentButton.clickToPayment" />
          </PrimaryButton>
        </div>
      ) : (
        <PrimaryButton
          className={css.paymentButton}
          onClick={handleOpenModal}
        >
          <FormattedMessage id="SubscriptionPaymentButton.clickToPayment" />
        </PrimaryButton>
      )}

      <SubscriptionCheckoutModal
        listing={listing}
        currentUser={currentUser}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSubscriptionSuccess={handleSubscriptionSuccess}
        onDirectPublish={handleDirectPublish}
        onManageDisableScrolling={onManageDisableScrolling}
      />
    </div>
  );
};

SubscriptionPaymentButton.propTypes = {
  className: string,
  rootClassName: string,
  listing: object.isRequired,
  currentUser: object,
  onManageDisableScrolling: func,
  onPaymentSuccess: func,
  showInline: bool, // If true, shows message with button inline
};

export default SubscriptionPaymentButton;
